import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PaginationComponent } from '../../../../components/inputs/pagination/pagination.component';
import { DataTableComponent } from '../../../../components/ui/data-table/data-table.component';
import { EmptyStateComponent } from '../../../../components/ui/empty-state/empty-state.component';
import { SmsTextComponent } from '../../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../../components/messages/delivery/delivery.component';
import { DeliveryService, DeliverySummary } from '../../../../services/message/delivery.service';
import { MessageService } from '../../../../services/message/message.service';
import { DeliveryReportService } from '../../../../services/reports/delivery-report.service';
import {
  DEFAULT_REPORT_PERIOD,
  ReportPeriodDays,
  periodPhrase,
} from '../../../../services/reports/report-period';
import { formatCount, formatWhen, sendMoment, smsParts } from '../../../../../utils/sms';

/** One sent message with what it cost in SMS parts and how it landed. */
interface SentView {
  key: string | number;
  /** The API record, handed to the message report as-is. */
  message: any;
  uuid: string;
  subject: string;
  copy: string;
  from: string;
  sentAt: Date | null;
  when: string;
  /** SMS parts per recipient; 0 when there is no copy. */
  parts: number;
  /** Personalised fields make the length differ per contact. */
  estimated: boolean;
  summary: DeliverySummary | null;
  summaryFailed: boolean;
}

/**
 * Messages that went out in the period, newest first: how each landed and
 * how many SMS parts it used (recipients × parts per message). Parts are
 * what the networks bill; the console has no tariff, so no currency.
 */
@Component({
  selector: 'sent-report',
  standalone: true,
  imports: [
    MatTooltipModule,
    PaginationComponent,
    DataTableComponent,
    EmptyStateComponent,
    SmsTextComponent,
    DeliveryComponent,
  ],
  templateUrl: './sent-report.component.html',
  styleUrls: ['../report-table.css', './sent-report.component.css'],
})
export class SentReportComponent implements OnInit, OnChanges {
  private reports = inject(DeliveryReportService);
  private delivery = inject(DeliveryService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input() days: ReportPeriodDays = DEFAULT_REPORT_PERIOD;
  /** Bumped by the page's Refresh button. */
  @Input() reloadKey = 0;

  readonly formatCount = formatCount;

  page = 0;
  size = 20;
  rows: SentView[] = [];
  total = 0;
  totalPages = 1;
  status: 'loading' | 'ready' | 'error' = 'loading';

  private load$ = new Subject<void>();
  private summaries = new Subscription();

  get periodText(): string {
    return periodPhrase(this.days);
  }

  /** Parts billed across the rows whose delivery counts have loaded. */
  get pageParts(): { parts: number; messages: number; pending: number; estimated: boolean } {
    let parts = 0;
    let messages = 0;
    let pending = 0;
    let estimated = false;
    for (const row of this.rows) {
      if (!row.summary) {
        if (!row.summaryFailed) pending += 1;
        continue;
      }
      parts += row.parts * row.summary.total;
      messages += 1;
      estimated = estimated || row.estimated;
    }
    return { parts, messages, pending, estimated };
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.summaries.unsubscribe());

    this.load$
      .pipe(
        switchMap(() => {
          this.status = 'loading';
          return this.reports.sentMessages(this.days, this.page, this.size).pipe(
            map((result) => ({ ...result, failed: false })),
            catchError(() => of({ rows: [] as any[], total: 0, totalPages: 1, failed: true }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ rows, total, totalPages, failed }) => {
        this.rows = toViews(rows);
        this.total = total;
        this.totalPages = totalPages;
        this.status = failed ? 'error' : 'ready';
        this.loadSummaries();
      });

    this.load$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const days = changes['days'];
    const reload = changes['reloadKey'];
    if ((days && !days.firstChange) || (reload && !reload.firstChange)) {
      this.page = 0;
      this.load$.next();
    }
  }

  reload(): void {
    this.load$.next();
  }

  onPage(page: number): void {
    this.page = Number(page) || 0;
    this.load$.next();
  }

  /** The pager follows a size change with changePage(0), which reloads. */
  onPageSize(size: number): void {
    this.size = Number(size) || 20;
    this.page = 0;
  }

  billable(row: SentView): string {
    if (!row.summary) return row.summaryFailed ? '—' : '…';
    if (!row.parts) return '—';
    return formatCount(row.parts * row.summary.total);
  }

  partsLabel(row: SentView): string {
    if (!row.parts) return '—';
    return row.parts === 1 ? '1 part' : `${row.parts} parts`;
  }

  openReport(row: SentView, failedOnly = false): void {
    if (!row.uuid) return;
    this.messageService.updateSelectedMessage(row.message);
    this.router.navigate(
      ['/organization/messages/report', row.uuid],
      failedOnly ? { queryParams: { status: 'failed' } } : {}
    );
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  openFromKeyboard(event: Event, row: SentView): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.openReport(row);
  }

  private loadSummaries(): void {
    this.summaries.unsubscribe();
    this.summaries = new Subscription();
    for (const row of this.rows) {
      if (!row.uuid) {
        row.summaryFailed = true;
        continue;
      }
      this.summaries.add(
        this.delivery.summary(row.uuid).subscribe({
          next: (summary) => (row.summary = summary),
          error: () => (row.summaryFailed = true),
        })
      );
    }
  }
}

function toViews(messages: unknown): SentView[] {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((message) => !!message && typeof message === 'object')
    .map((message: any, index): SentView => {
      const copy = String(message.content || message.messageTemplate || '');
      const count = copy ? smsParts(copy) : null;
      const sentAt = sendMoment(message);
      return {
        key: message.id ?? message.uuid ?? `row-${index}`,
        message,
        uuid: String(message.uuid ?? ''),
        subject: String(message.messageSubject ?? '').trim() || 'Untitled message',
        copy,
        from: String(message.from ?? '').trim(),
        sentAt,
        when: formatWhen(sentAt),
        parts: count?.parts ?? 0,
        estimated: !!count?.estimated,
        summary: null,
        summaryFailed: false,
      };
    });
}
